import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import type {
  SearchExplainContentMetric,
  SearchExplainQueryMetrics,
} from 'bundles/search-common/components/internal-search-explainer/types';
import NostosSearchExplainerMetricsQuery from 'bundles/search-common/queries/NostosSearchExplainerMetrics.graphql';
import type {
  NostosSearchExplainerMetricsQuery as NostosSearchExplainerMetricsQueryResponse,
  NostosSearchExplainerMetricsQueryVariables,
} from 'bundles/search-common/queries/__generated__/NostosSearchExplainerMetrics';

/**
 * Registry id of `nostos.search_explainer_query_metrics` in the Nostos v3 table registry.
 * The API addresses tables by opaque id only — there is no name resolution. Ids are assigned
 * per environment, and the explainer only runs in production, so the prod id is hardcoded.
 */
const SEARCH_EXPLAINER_METRICS_TABLE_ID = '170';

type NostosEntry = NonNullable<
  NonNullable<NostosSearchExplainerMetricsQueryResponse['Nostos']>['getData']
>['entries'][number];

const getColumnValue = (entry: NostosEntry, column: string): string | undefined =>
  entry.data.find((columnData) => columnData.column === column)?.value;

const parseJsonValue = (value: string | undefined): unknown => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

/**
 * Column `content` is a JSON object of query-level scalars and column `content_metrics` is a
 * JSON array of per-product metrics. Both arrive camelCase (the RETL job aggregates with
 * `camelify_keys`), so the two columns just need merging into the single object the explainer
 * renders.
 */
const adaptEntry = (entry: NostosEntry): SearchExplainQueryMetrics | null => {
  const scalars = parseJsonValue(getColumnValue(entry, 'content'));
  if (!scalars || typeof scalars !== 'object' || Array.isArray(scalars)) {
    return null;
  }

  const contentMetrics = parseJsonValue(getColumnValue(entry, 'content_metrics'));
  return {
    ...(scalars as Omit<SearchExplainQueryMetrics, 'content_metrics'>),
    content_metrics: Array.isArray(contentMetrics) ? (contentMetrics as SearchExplainContentMetric[]) : [],
  };
};

/**
 * Hook to fetch search explain metrics from the Nostos v3 API
 * When disabled, returns early without making API calls
 */
export const useSearchExplainMetrics = (query: string, enabled: boolean) => {
  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);
  const shouldFetch = enabled && !!normalizedQuery;

  const { data, loading, error } = useQuery<
    NostosSearchExplainerMetricsQueryResponse,
    NostosSearchExplainerMetricsQueryVariables
  >(NostosSearchExplainerMetricsQuery, {
    variables: {
      input: { tableId: SEARCH_EXPLAINER_METRICS_TABLE_ID, keys: [normalizedQuery] },
    },
    skip: !shouldFetch,
    // The Nostos field only exists on the gateway schema.
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const metrics = useMemo(() => {
    const entry = data?.Nostos?.getData?.entries?.[0];
    return entry ? adaptEntry(entry) : null;
  }, [data]);

  return {
    metrics,
    loading: shouldFetch ? loading : false,
    error,
  };
};

export default useSearchExplainMetrics;
